export const STYLE_HREF = '../css/style.css';

// Fallback configuration used when the host page hasn't defined
// `window.ILoveBasketConfig` before the widgets bundle loads.
const DEFAULT_CONFIG = {
    apiUrl: 'https://api.ilovebasketball.ru',
    urls: {
        game: '/game/{gameId}',
        team: '/team/{teamId}',
        player: '/player/{playerId}',
        coach: '/coach/{coachId}',
        comp: '/comp/{compId}'
    }
};

// Returns the global widgets configuration, initializing it with the
// defaults on first access if the host page hasn't provided one.
export function getConfig() {
    if (typeof window === 'undefined') {
        return DEFAULT_CONFIG;
    }
    if (!window.ILoveBasketConfig) {
        window.ILoveBasketConfig = DEFAULT_CONFIG;
    }
    return window.ILoveBasketConfig;
}

export function getApiUrl() {
    return getConfig().apiUrl || DEFAULT_CONFIG.apiUrl;
}

function resolveUrlTemplate(name) {
    return getConfig().urls?.[name] || DEFAULT_CONFIG.urls[name];
}

// Substitutes the single `{...}` placeholder in a URL template with `id`
// and appends any extra params as a query string.
function buildEntityUrl(templateName, id, queryParams) {
    const template = resolveUrlTemplate(templateName);
    const path = template.replace(/\{[^}]+\}/, encodeURIComponent(id ?? ''));
    return path + buildQuery(queryParams);
}

// Builds a link to a game page. `options` may include `tag` and `compId`.
export function gameUrl(gameId, options = {}) {
    const { tag, compId } = options;
    return buildEntityUrl('game', gameId, { tag, compId });
}

// Builds a link to a team page. `options` may include `tag` and `compId`.
export function teamUrl(teamId, options = {}) {
    const { tag, compId } = options;
    return buildEntityUrl('team', teamId, { tag, compId });
}

// Builds a link to a coach page. `options` may include `tag`, `teamId` and `compId`.
export function coachUrl(personId, options = {}) {
    const { tag, teamId, compId } = options;
    return buildEntityUrl('coach', personId, { tag, teamId, compId });
}

// Builds a link to a player page. `options` may include `tag`, `teamId` and `compId`.
export function playerUrl(personId, options = {}) {
    const { tag, teamId, compId } = options;
    return buildEntityUrl('player', personId, { tag, teamId, compId });
}

// Builds a link to a competition page. `options` may include `tag`, `season` and `lang`.
export function compUrl(compId, options = {}) {
    const { tag, season, lang } = options;
    return buildEntityUrl('comp', compId, { tag, season, lang });
}

export function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            search.set(key, String(value));
        }
    });
    const query = search.toString();
    return query ? `?${query}` : '';
}

export async function loadJson(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    const payload = await response.json();
    return payload?.result ?? payload;
}

export function normalizeText(value, fallback = '—') {
    return value === null || value === undefined || value === '' ? fallback : String(value);
}

export function normalizeCollection(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.$values)) return payload.$values;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
}

export function getStatValue(stats, key, fallback = '—') {
    return normalizeText(stats?.[key], fallback);
}

export function getDivisionTitle(division, fallback = 'Без названия') {
    const fullName = division?.fullName;
    if (typeof fullName === 'string' && fullName) return fullName;
    if (fullName && typeof fullName === 'object') {
        return fullName.ru || fullName.en || Object.values(fullName)[0] || division?.id || fallback;
    }
    return division?.id || fallback;
}

export function injectGlobalStyles(shadowRoot) {
    if (!shadowRoot) {
        return;
    }

    if (!shadowRoot.querySelector('link[data-widget-style]')) {
        const link = document.createElement('link');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', STYLE_HREF);
        link.setAttribute('data-widget-style', '1');
        shadowRoot.prepend(link);
    }
}

export function htmlEscape(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}
